import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { debounce, throttle } from 'underscore';

type TTrackScrollPosition = {
  containerRef?: RefObject<HTMLElement | null> | null;
  debounceTime?: number;
  throttleTime?: number;
  trackType?: 'debounced' | 'throttled' | 'fast' | 'debounced_and_throttled';
  pause?: boolean;
};

const useTrackScrollDimensions = ({
  containerRef,
  debounceTime = 50,
  throttleTime = 250,
  trackType = 'fast',
  pause,
}: TTrackScrollPosition) => {
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);

  const calculatePositions = useCallback(() => {
    const el = containerRef?.current;
    if (!el) return;

    setTop(el.scrollTop);
    setLeft(el.scrollLeft);
  }, [containerRef]);

  const frameRef = useRef(0);

  useEffect(() => {
    if (pause) return;

    const el = containerRef?.current;

    if (!el) return;

    // initialize
    calculatePositions();

    const fastThrottled = () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = requestAnimationFrame(() => {
        calculatePositions();
      });
    };

    fastThrottled();

    const debounced = debounce(fastThrottled, debounceTime);
    const throttled = throttle(fastThrottled, throttleTime);

    const isDebounced = trackType === 'debounced' || trackType === 'debounced_and_throttled';
    const isThrottled = trackType === 'throttled' || trackType === 'debounced_and_throttled';
    const isFast = trackType === 'fast';

    if (el) {
      if (isDebounced) {
        el.addEventListener('scroll', debounced, { passive: true });
      }
      if (isThrottled) {
        el.addEventListener('scroll', throttled, { passive: true });
      }
      if (isFast) {
        el.addEventListener('scroll', fastThrottled, { passive: true });
      }
    } else {
      if (isDebounced) {
        window.addEventListener('scroll', debounced, { passive: true });
      }
      if (isThrottled) {
        window.addEventListener('scroll', throttled, { passive: true });
      }
      if (isFast) {
        window.addEventListener('scroll', fastThrottled, { passive: true });
      }
    }

    return () => {
      if (el) {
        el.removeEventListener('scroll', debounced);
        el.removeEventListener('scroll', throttled);
        el.removeEventListener('scroll', fastThrottled);
      }
      window.removeEventListener('scroll', debounced);
      window.removeEventListener('scroll', throttled);
      window.removeEventListener('scroll', fastThrottled);
    };
  }, [containerRef, debounceTime, throttleTime, trackType, calculatePositions, pause]);

  return { top, left };
};

export default useTrackScrollDimensions;
